import { boolean, mixed, object, string } from "yup";
import { useEffect, useState } from "react";
import { useForm, useFormState } from "react-hook-form";

import Validations from "@helpers/validations";
import { defaultValues } from "../misc/constants";
import useAxios from "@hooks/use-axios";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

const useUpsert = props => {
    const { platformId } = useParams();
    
    const {
        modalConfig: { row },
        handleModal,
        getAllSurveys
    } = props;
    const [activeAccordion, setActiveAccordion] = useState("general");

    const { loading: updateLoading, axiosInstance: upsertAxiosInstance } =
        useAxios({ base: "panel" });
        
    const { loading, axiosInstance } = useAxios({
        base         : "panel",
        initialLoader: !!row._id
    });

    const { requiredValidation } = Validations();
    const [activeId, setActiveId] = useState({
        additionalComment: "",
        thankYouMessage  : ""
    });
    const validationSchema = object().shape({
        _id : string().default(""),
        name: requiredValidation({ name: "Name" }),

        survey_type: mixed()
            .oneOf(["", "csat", "nps"], "Survey Type is invalid")
            .required("Survey Type is required"),

        additional_message: string().default(""),
        question_message  : string().default(
            "How likely are you to recommend our service to others?"
        ),
        bad_rating_text           : string().default("Not likely at all"),
        good_rating_text          : string().default("Extremely likely"),
        enable_additional_comments: boolean().default(false),

        additional_comments: object().shape({
            promoter: string().default(
                "What made your experience exceptional?"
            ),
            passive  : string().default("What could we improve?"),
            detractor: string().default("How can we make things right?"),
            satisfied: string().default(
                "What made your experience exceptional?"
            ),
            unsatisfied: string().default("What could we improve?"),
            neutral    : string().default("How can we make things right?")
        }),

        response_values: object().shape({
            very_satisfied  : string().default("Very Satisfied"),
            satisfied       : string().default("Satisfied"),
            neutral         : string().default("Neutral"),
            unsatisfied     : string().default("Unsatisfied"),
            very_unsatisfied: string().default("Very Unsatisfied")
        }),

        thank_you_messages: object().shape({
            promoter: string().default(
                "Thank you for your fantastic feedback! We're thrilled you had a great experience."
            ),
            passive: string().default(
                "Thank you for your feedback. We appreciate your input and will work on improving."
            ),
            detractor: string().default(
                "We apologize for not meeting your expectations. Thank you for helping us improve."
            ),
            satisfied: string().default(
                "Thank you for your fantastic feedback! We're thrilled you had a great experience."
            ),
            neutral: string().default(
                "Thank you for your feedback. We appreciate your input and will work on improving."
            ),
            unsatisfied: string().default(
                "We apologize for not meeting your expectations. Thank you for helping us improve."
            )
        })
    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema),
        mode    : "onChange"
    });

    const { control, handleSubmit, reset } = methods;
    const { isDirty } = useFormState({ control });

    const handleActiveId = (key, value) => {
        if (key === "enable_additional_comments") {
            setActiveId({
                thankYouMessage  : "",
                additionalComment: value
            });
        } else {
            setActiveId({
                ...activeId,
                [key]: value
            });
        }
    };
    const handleActiveAccordion = (tab) => {
        handleActiveId("thankYouMessage", "");
        setActiveAccordion(tab);
    };
    const onSubmit = handleSubmit(async data => {
        const payload = {
            name                      : data.name,
            survey_type               : data.survey_type,
            additional_message        : data.additional_message,
            question_message          : data.question_message,
            bad_rating_text           : data.bad_rating_text,
            good_rating_text          : data.good_rating_text,
            enable_additional_comments: data.enable_additional_comments,

            additional_comments: {
                promoter   : data.additional_comments.promoter,
                passive    : data.additional_comments.passive,
                detractor  : data.additional_comments.detractor,
                satisfied  : data.additional_comments.satisfied,
                unsatisfied: data.additional_comments.unsatisfied,
                neutral    : data.additional_comments.neutral
            },

            response_values: {
                very_satisfied  : data.response_values.very_satisfied,
                satisfied       : data.response_values.satisfied,
                neutral         : data.response_values.neutral,
                unsatisfied     : data.response_values.unsatisfied,
                very_unsatisfied: data.response_values.very_unsatisfied
            },

            thank_you_messages: {
                promoter   : data.thank_you_messages.promoter,
                passive    : data.thank_you_messages.passive,
                detractor  : data.thank_you_messages.detractor,
                satisfied  : data.thank_you_messages.satisfied,
                neutral    : data.thank_you_messages.neutral,
                unsatisfied: data.thank_you_messages.unsatisfied
            }
        };

        if (row?._id) {
            await upsertAxiosInstance.patch(`/survey/${platformId}/${row._id}`, payload);
        } else {
            await upsertAxiosInstance.post(`/survey/${platformId}`, payload);
        }
        handleModal();
        getAllSurveys();
    });

    const getSurveyDetails = async id => {
        const { data } = await axiosInstance.get(`/survey/detail?survey_id=${id}&orid=${row.orid}&pid=${platformId}`);
        reset({
            ...defaultValues,
            ...data,
            survey_type: data.survey_type 
        });
    };

    useEffect(() => {
        if (row._id) {
            getSurveyDetails(row._id);
        } else {
            reset({
                ...defaultValues,
                survey_type: row.survey_type
            });
        }
    }, [row]);

    return {
        onSubmit,
        methods,
        updateLoading,
        isDirty,
        handleActiveId,
        activeId,
        loading,
        handleActiveAccordion,
        activeAccordion
    };
};

export default useUpsert;
